/**
 * Service module for submitting user feedback via email API.
 *
 * This module handles the communication between the feedback form and the
 * backend email API endpoint, formatting the feedback data into the expected
 * email payload structure.
 */

import { APP_CONFIG } from '../configs/config';
import { COMPANY_INFO } from '../configs/company';

/**
 * Sends user feedback to the configured target email address.
 *
 * @param {Object} formData - The feedback form data.
 * @param {string} formData.name - Submitter's name.
 * @param {string} formData.email - Submitter's email address.
 * @param {string} formData.category - Feedback category/type.
 * @param {string} formData.message - Feedback message content.
 * @param {string|null} username - Authenticated user's username (optional).
 * @returns {Promise<Object>} - Response data from the email API.
 * @throws {Error} - If the request fails or the server returns an error.
 */
export const sendFeedback = async (formData, username = null) => {
  const emailPayload = {
    username: formData.name,
    target_address: COMPANY_INFO.contact.email.feedback,
    email: formData.email,
    message: formData.message,
    title: `Business Card App - Feedback - ${formData.category}`,
    content: {
      'Logged-in user': username || 'Not logged in',
      'Submitter name': formData.name,
      'Submitter email': formData.email,
      'Feedback category': formData.category,
      'Message': formData.message,
      'Submitted at': new Date().toISOString()
    }
  };

  const response = await fetch(
    `${APP_CONFIG.MAIN_WEBSITE_API_URL}/email/send`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(emailPayload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Failed to send feedback. Please try again.'
    );
  }

  return await response.json();
};

